import type { SpoilerLevel } from "@/lib/timeline/view/TimelineUiPreferences/prefs";
import { useEffect, useRef, useState } from "react";

import type { SceneSpoiler } from "@/data/schema/scene";

import { activeBeat, sceneTotal, type BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import { chapterNumber, formatChapterHeading } from "@/lib/timeline/book/ChapterId";
import {
  formatPlaybackPlayheadMs,
  type PlaybackState,
} from "@/lib/timeline/playback";
import { CompendiumText } from "../CompendiumText";
import { TimelineSceneSpoilers } from "./TimelineSceneSpoilers";
import { useBookChapterCount } from "./useBookChapterCount";

const NO_SPOILERS: readonly SceneSpoiler[] = [];

interface TimelineSceneCaptionProps {
  canvas: HTMLCanvasElement | null;
  layout: BookTimelineLayout;
  getPlayback: () => PlaybackState;
  spoilerLevel: SpoilerLevel;
  /** Board-model debug toggle (staging); shows scene numbers and the live story playhead. */
  playbackDebugVisible?: boolean;
}

export function TimelineSceneCaption({
  canvas,
  layout,
  getPlayback,
  spoilerLevel,
  playbackDebugVisible = false,
}: TimelineSceneCaptionProps) {
  const [chapterHeading, setChapterHeading] = useState(
    chapterHeadingFor(layout, layout.beats[0]),
  );
  const [title, setTitle] = useState(layout.beats[0]?.title ?? "");
  const [sceneNumber, setSceneNumber] = useState(layout.beats[0]?.sceneNumber);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(layout.beats[0]?.summary ?? "");
  const [sceneId, setSceneId] = useState(layout.beats[0]?.sceneId ?? "");
  const [spoilers, setSpoilers] = useState(layout.beats[0]?.spoilers ?? NO_SPOILERS);
  const [storyTime, setStoryTime] = useState(layout.beats[0]?.storyTimeLabel ?? "");
  const [playheadLabel, setPlayheadLabel] = useState("");
  const [readingScene, setReadingScene] = useState<string | null>(null);
  const [chapterId, setChapterId] = useState(layout.beats[0]?.chapterId);
  const chapterCount = useBookChapterCount(playbackDebugVisible ? layout.bookId : undefined);
  // Spoilers opened or closed by hand stay that way when the scene comes back around.
  const spoilersOpened = useRef(new Map<string, boolean>());
  useEffect(() => {
    spoilersOpened.current.clear();
  }, [spoilerLevel]);

  // Opened text closes with a tap anywhere else, so it never sits over the story.
  useEffect(() => {
    if (readingScene === null) {
      return;
    }
    const onPress = (event: PointerEvent): void => {
      if (!(event.target as Element).closest?.(".timeline-scene-overlay__summary")) {
        setReadingScene(null);
      }
    };
    window.addEventListener("pointerdown", onPress);
    return () => window.removeEventListener("pointerdown", onPress);
  }, [readingScene]);

  useEffect(() => {
    if (canvas === null) {
      return;
    }
    const onFrame = (): void => {
      const playback = getPlayback();
      const beat = activeBeat(layout, playback.playheadMs);
      if (beat === undefined) {
        return;
      }
      const nextTime = beat.storyTimeLabel ?? "";
      const nextChapter = chapterHeadingFor(layout, beat);
      setChapterHeading((prev) => (prev === nextChapter ? prev : nextChapter));
      setChapterId((prev) => (prev === beat.chapterId ? prev : beat.chapterId));
      setTitle((prev) => (prev === beat.title ? prev : beat.title));
      setSceneNumber((prev) => (
        prev === beat.sceneNumber ? prev : beat.sceneNumber
      ));
      const nextTotal = sceneTotal(layout, beat);
      setTotal((prev) => (prev === nextTotal ? prev : nextTotal));
      setSummary((prev) => (prev === beat.summary ? prev : beat.summary));
      setSceneId((prev) => (prev === beat.sceneId ? prev : beat.sceneId));
      const nextSpoilers = beat.spoilers ?? NO_SPOILERS;
      setSpoilers((prev) => (prev === nextSpoilers ? prev : nextSpoilers));
      setStoryTime((prev) => (prev === nextTime ? prev : nextTime));
      if (playbackDebugVisible) {
        const nextPlayhead = formatPlaybackPlayheadMs(playback.playheadMs);
        setPlayheadLabel((prev) => (prev === nextPlayhead ? prev : nextPlayhead));
      }
    };
    canvas.addEventListener("timeline-frame", onFrame);
    onFrame();
    return () => canvas.removeEventListener("timeline-frame", onFrame);
  }, [canvas, getPlayback, layout, playbackDebugVisible]);

  return (
    <>
      <div className="timeline-scene-overlay">
        <div className="timeline-scene-overlay__text">
          {chapterHeading.length > 0 ? (
            <p className="timeline-scene-overlay__chapter">
              {playbackDebugVisible && chapterId !== undefined && chapterCount !== undefined ? (
                <span className="timeline-scene-overlay__scene-no">
                  {chapterNumber(chapterId)}/{chapterCount}
                </span>
              ) : null}
              {chapterHeading}
            </p>
          ) : null}
          <p className="timeline-scene-overlay__title">
            {playbackDebugVisible && sceneNumber !== undefined ? (
              <span className="timeline-scene-overlay__scene-no">
                {sceneNumber}/{total}
              </span>
            ) : null}
            <CompendiumText text={title} bookId={layout.bookId} newTab />
          </p>
          {summary.length > 0 ? (
            <p
              className="timeline-scene-overlay__summary"
              data-reading={readingScene === sceneId}
              onClick={() => setReadingScene(readingScene === sceneId ? null : sceneId)}
            >
              <CompendiumText text={summary} bookId={layout.bookId} newTab />
            </p>
          ) : null}
          {spoilerLevel !== "off" && spoilers.length > 0 ? (
            <TimelineSceneSpoilers
              key={`${sceneId}:${spoilerLevel}`}
              spoilers={spoilers}
              spoilersOn={spoilersOpened.current.get(sceneId) ?? spoilerLevel === "shown"}
              bookId={layout.bookId}
              onToggle={(open) => {
                spoilersOpened.current.set(sceneId, open);
              }}
            />
          ) : null}
        </div>
      </div>
      {playbackDebugVisible || storyTime.length > 0 ? (
        <div className="timeline-story-time-stack">
          {playbackDebugVisible ? (
            <p className="timeline-story-time-debug">{playheadLabel}</p>
          ) : null}
          {storyTime.length > 0 ? (
            <p className="timeline-story-time">{storyTime}</p>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

function chapterHeadingFor(
  layout: BookTimelineLayout,
  beat: BookTimelineLayout["beats"][number] | undefined,
): string {
  if (layout.journey === "universe") {
    return beat?.storyDateLabel ?? "";
  }
  if (beat?.chapterId === undefined || beat.chapterId.length === 0) {
    return "";
  }
  return formatChapterHeading(beat.chapterId, beat.chapterTitle ?? layout.title);
}
